import type { AppliedChatbotConfig, ChatbotLogger } from '../types';

/**
 * Creates and injects the <applied-chat> web component.
 */
export function injectChatElement(
  config: AppliedChatbotConfig,
  logger: ChatbotLogger | Console = console,
): void {
  if (typeof document === 'undefined') return;
  if (!config.agentId) {
    logger.error({
      type: 'client',
      context: 'Chatbot - injectChatElement',
      message: 'No agent ID provided',
      sessionId: undefined,
    });
    return;
  }

  try {
    const {
      agentId,
      userId,
      metadata,
      groups,
      theme = 'light',
      size = 'sm',
      hide = false,
    } = config;

    // Remove any existing element
    removeChatElement(logger);

    const chatEl = document.createElement('applied-chat');
    chatEl.setAttribute('agent-id', agentId);
    chatEl.setAttribute('theme', theme);
    chatEl.setAttribute('size', size);
    if (userId) chatEl.setAttribute('user-id', userId);
    if (hide) chatEl.setAttribute('hide', '');

    if (metadata || groups) {
      const serialized = JSON.stringify({
        context: metadata?.context,
        groups,
      });
      chatEl.setAttribute('metadata', serialized);
    }

    document.body.appendChild(chatEl);
    logger.info({
      type: 'client',
      context: 'Chatbot - injectChatElement',
      message: '<applied-chat> element injected',
      sessionId: undefined,
    });
  } catch (err) {
    logger.error(
      {
        type: 'client',
        context: 'Chatbot - injectChatElement',
        message: 'Failed to inject <applied-chat>',
        sessionId: undefined,
      },
      { error: err },
    );
  }
}

/**
 * Removes the <applied-chat> element from the DOM if it exists.
 */
export function removeChatElement(logger: ChatbotLogger | Console = console): void {
  try {
    const el = document.querySelector('applied-chat');
    if (el) {
      el.remove();
      logger.info({
        type: 'client',
        context: 'Chatbot - removeChatElement',
        message: '<applied-chat> element removed',
        sessionId: undefined,
      });
    }
  } catch (err) {
    logger.error(
      {
        type: 'client',
        context: 'Chatbot - removeChatElement',
        message: 'Failed to remove <applied-chat>',
        sessionId: undefined,
      },
      { error: err },
    );
  }
}

/**
 * Checks whether <applied-chat> is currently in the DOM.
 */
export function isChatElementMounted(): boolean {
  return typeof document !== 'undefined' && !!document.querySelector('applied-chat');
}
